import type { Dispatcher } from "undici";
import type { HttpClient, HttpEvent } from "./types.js";
import type { RetryOptions, RateLimitOptions, TransportConfig, TransportMode } from "../data-model/options.js";
import type { Logger } from "../utils/logger.js";
import type { Url } from "../data-model/primitives.js";
import { silentLogger } from "../utils/logger.js";
import { UndiciHttpClient } from "./client.js";
import { UserAgents, UserAgentRotatingHttpClient } from "./user-agents.js";
import { RateLimiter, RateLimitedHttpClient, DEFAULT_RATE_LIMIT } from "./rate-limiter.js";
import { RetryingHttpClient, DEFAULT_RETRY } from "./retry.js";
import { CookieJar } from "./cookie-jar.js";
import { TieredHttpClient } from "./tiered.js";
import type { CookieCaptureResult } from "./tiered.js";

export interface HttpStackOptions {
  readonly retry?: RetryOptions;
  readonly rateLimit?: RateLimitOptions;
  readonly rateLimiter?: RateLimiter;
  readonly userAgents?: UserAgents;
  readonly logger?: Logger;
  readonly defaultHeaders?: Readonly<Record<string, string>>;
  readonly onEvent?: (e: HttpEvent) => void;
  /** Transport configuration. Defaults to `{ mode: "auto" }`. */
  readonly transport?: TransportConfig;
  /** Cookie jar shared between the HTTP branch and the browser branch. */
  readonly cookieJar?: CookieJar;
  /**
   * Returns the preferred transport for a given URL.
   * Defaults to always returning `"http"`.
   */
  readonly preferredTransport?: (url: Url) => "http" | "browser";
  /**
   * Factory called once to create the browser-side `HttpClient`.
   * When omitted, a lazy fallback is used that throws
   * `BrowserModuleNotInstalledError` on first `get()` call.
   */
  readonly browserBranchFactory?: () => HttpClient;
  /**
   * Called after each successful browser-branch request to capture
   * Cloudflare cookies + the user-agent string the browser used.
   * Defaults to a no-op that returns empty cookies.
   */
  readonly onCookieCapture?: (host: string) => Promise<CookieCaptureResult>;
  /**
   * Test seam: replaces the real `UndiciHttpClient` with a fake.
   * Do not use in production code.
   */
  readonly undiciOverride?: HttpClient;
  /**
   * undici dispatcher for the leaf HTTP client. Pass one so its lifecycle is
   * owned by the caller (closed on `dispose()`) and requests never route
   * through the process-global dispatcher. When omitted, the leaf creates and
   * owns its own `Agent`.
   */
  readonly dispatcher?: Dispatcher;
}

export function normalizeTransport(t: TransportConfig | TransportMode | undefined): TransportConfig {
  if (t === undefined) return { mode: "auto" };
  if (typeof t === "string") return { mode: t };
  return t;
}

export function buildHttpClient(opts: HttpStackOptions = {}): HttpClient {
  const logger = opts.logger ?? silentLogger;
  const cookieJar = opts.cookieJar ?? new CookieJar();

  // Leaf HTTP client (real undici or test override)
  const leaf: HttpClient =
    opts.undiciOverride ??
    new UndiciHttpClient({
      logger,
      ...(opts.dispatcher !== undefined ? { dispatcher: opts.dispatcher } : {}),
    });
  const limiter = opts.rateLimiter ?? new RateLimiter(opts.rateLimit ?? DEFAULT_RATE_LIMIT);

  // Retry wraps rate-limit wraps UA-rotation: every retry attempt re-enters
  // the rate limiter and picks a fresh user-agent before hitting the leaf.
  const withUserAgent = new UserAgentRotatingHttpClient(
    leaf,
    opts.userAgents ?? new UserAgents(),
    opts.defaultHeaders,
    cookieJar,
  );
  const withRateLimit = new RateLimitedHttpClient(withUserAgent, limiter, opts.onEvent);
  const httpBranch: HttpClient = new RetryingHttpClient(
    withRateLimit,
    normalizeRetry(opts.retry),
    logger,
    limiter,
    opts.onEvent,
  );

  const { mode } = normalizeTransport(opts.transport);

  if (mode === "http-only") {
    return httpBranch;
  }

  // Build the browser branch
  const browserBranch: HttpClient = opts.browserBranchFactory != null
    ? opts.browserBranchFactory()
    : {
        async get() {
          // Lazy import so the error class is not loaded at module init time.
          const { BrowserModuleNotInstalledError } = await import("../core/errors.js");
          throw new BrowserModuleNotInstalledError();
        },
      };

  return new TieredHttpClient({
    httpBranch,
    browserBranch,
    cookieJar,
    mode,
    preferredTransport: opts.preferredTransport ?? (() => "http"),
    onCookieCapture: opts.onCookieCapture ?? (async () => ({ cookies: [], ua: null })),
    logger,
  });
}

function normalizeRetry(opts?: RetryOptions): Required<RetryOptions> {
  return {
    retries: opts?.retries ?? DEFAULT_RETRY.retries,
    backoff: opts?.backoff ?? DEFAULT_RETRY.backoff,
    minDelayMs: opts?.minDelayMs ?? DEFAULT_RETRY.minDelayMs,
    maxDelayMs: opts?.maxDelayMs ?? DEFAULT_RETRY.maxDelayMs,
    retryOn: opts?.retryOn ?? DEFAULT_RETRY.retryOn,
  };
}

export type { HttpClient, HttpRequestOptions, HttpResponse, HttpEvent } from "./types.js";
export { RateLimiter, RateLimitedHttpClient, DEFAULT_RATE_LIMIT } from "./rate-limiter.js";
export { RetryingHttpClient, DEFAULT_RETRY } from "./retry.js";
export { UserAgents, UserAgentRotatingHttpClient, DEFAULT_UA_POOL } from "./user-agents.js";
export { UndiciHttpClient } from "./client.js";
export { CookieJar } from "./cookie-jar.js";
export { TieredHttpClient } from "./tiered.js";
export type { CookieCaptureResult } from "./tiered.js";
